import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Card, StyledAction, StyledBody } from "baseui/card";
import { ANCHOR, Drawer, SIZE } from "baseui/drawer";
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { FormControl } from 'baseui/form-control';
import { ChevronLeft, ChevronRight, Delete } from "baseui/icon";
import { DURATION, useSnackbar } from "baseui/snackbar";
import { HeadingXLarge, ParagraphMedium, ParagraphSmall } from 'baseui/typography';
import { forwardRef, useEffect, useRef, useState } from "react";
import { useDispatch } from 'react-redux';
import { deleteSolutionPhoto, submitSolution, uploadSolutionPhotos } from "../application/actions/solutions";
import { routeReplace, routeTo } from "../application/actions/ui";
import create_solution from '../assets/images/create_solution.png';
import { ReactComponent as Diagram } from '../assets/images/diagram.svg';
import useBreakpoint from "../hooks/useBreakpoint";
import useFormBuffer from "../hooks/useFormBuffer";
import useLocale from "../hooks/useLocale";
import ReDoBanner from "./ReDoBanner";
import ReDoButton from "./ReDoButton";
import ReDoCheckbox from "./ReDoCheckbox";
import ReDoFileUpload from "./ReDoFileUpload";
import ReDoInterests from "./ReDoInterests";
import ReDoProgressBar from "./ReDoProgressBar";
import ReDoSpinner from './ReDoSpinner';
import ReDoTags from './ReDoTags';
import ReDoTextarea from "./RedoTextarea";


const areas = [
  'Energy',
  'Water',
  'Waste',
  'Procurement',
  'Materials',
  'Communication',
  'Supply Chains',
  'Packaging',
  'End of Life',
  'Infrastructure',
  'Climate Adaption',
]

const ReDoCreateSolution = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const breakpoint = useBreakpoint()

  const { enqueue } = useSnackbar();
  const { readBuffer, writeBuffer, clearBuffer } = useFormBuffer('createSolution');
  const { t } = useLocale();

  const stages = [
    { label: t('createSolution.stage_title'), curr: 1, max: 9 },
    { label: t('createSolution.stage_status_quo'), curr: 2, max: 9 },
    { label: t('createSolution.stage_solution'), curr: 3, max: 9 },
    { label: t('createSolution.stage_value'), curr: 4, max: 9 },
    { label: t('createSolution.stage_life_cycle'), curr: 5, max: 9 },
    { label: t('createSolution.stage_areas'), curr: 6, max: 9 },
    { label: t('createSolution.stage_why'), curr: 7, max: 9 },
    { label: t('createSolution.stage_visuals'), curr: 8, max: 9 },
    { label: t('createSolution.stage_stage'), curr: 9, max: 9 },
  ]

  const solutionStage = [
    { group: '', list: ['EARLY \n(Idea or concept stage)', 'MID \n(First proof of concept exists, first user testing done)', 'FINAL \n(Prototype exists, research and testing results exist)'], color: '#fff' },
  ]

  const rawSaved = props.solution?.id ? null : readBuffer();
  const isTaintedSaved = !!rawSaved?.payload?.id;
  const saved = isTaintedSaved ? null : rawSaved;
  const lastStage = stages[stages.length - 1].curr;
  const [stageCurr, setStageCurr] = useState(props.solution?.id ? lastStage : (saved ? saved.stage : 1))
  const stage = stages.find(s => s.curr === stageCurr) || stages[0]
  const [payload, setPayload] = useState(saved?.payload || props.solution || {})

  const [photos, setPhotos] = useState([])
  const [removedPhotos, setRemovedPhotos] = useState([])
  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(false)

  const scrollable = useRef()

  useEffect(() => {
    if (isTaintedSaved) clearBuffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!props.solution?.id) {
      writeBuffer(stageCurr, payload);
    }
  }, [stageCurr, payload, writeBuffer, props.solution?.id])

  const column = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start',
    marginTop: '7ch',
    gap: '3ch'
  });

  const onNext = (e) => {
    e.preventDefault();
    scrollable.current.scrollTo(0, 0)

    const data = new FormData(e.target);
    const stepPayload = {}

    for (const pair of data.entries()) {
      if (stage.curr === 3 && pair[0] === 'type') {


        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else if (stage.curr === 9 && pair[0] === 'stage') {
        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else {
        stepPayload[pair[0]] = pair[1]
      }
    }

    const finalPayload = { ...payload, ...stepPayload }

    setPayload(finalPayload)

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (currStage < stages.length - 1) {
      setStageCurr(stages[currStage + 1].curr)
    } else {
      setLoading(true)
      if (props.challenge?.id) {
        finalPayload.challengeId = props.challenge.id
      }
      dispatch(submitSolution(finalPayload)).then(async res => {
        let solution = res
        // the update endpoint preserves existing photos, so removed ones must be deleted explicitly
        if (props.solution?.id && removedPhotos.length) {
          const deletedIds = new Set()
          for (const photo of removedPhotos) {
            try {
              await dispatch(deleteSolutionPhoto(props.solution.id, photo.id))
              deletedIds.add(photo.id)
            } catch (e) {
              // non-fatal: continue even if delete fails
            }
          }
          if (deletedIds.size > 0) {
            solution = { ...solution, photos: (solution.photos || []).filter(p => !deletedIds.has(p.id)) }
          }
        }
        // If backend update response omits photos, carry the remaining set forward from the payload
        if (props.solution?.id && !solution.photos?.length && finalPayload.photos?.length) {
          solution = { ...solution, photos: finalPayload.photos }
        }
        if (photos.length) {
          for (const photo of photos) {
            try {
              const photoRes = await dispatch(uploadSolutionPhotos({ solution: solution.id, title: photo.file?.file?.name ?? photo.file?.name ?? photo.title, photo: photo.photo }))
              solution = photoRes
            } catch (e) {
              setLoading(false)
              enqueue({ message: e.message + ' ' + e.response?.data?.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> });
            }
          }
        }
        setLoading(false)
        if (!props.solution?.id) clearBuffer()
        const navigate = props.solution?.id ? routeReplace : routeTo
        dispatch(navigate({ view: 'solution', payload: { solution } }))
      }).catch(e => {
        setLoading(false)
        enqueue({ message: e.response.data.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> })
      })

    }
  }

  const onPrev = (e) => {
    e.preventDefault();
    scrollable.current && scrollable.current.scrollTo(0, 0)

    const formEl = scrollable.current instanceof HTMLFormElement
      ? scrollable.current
      : scrollable.current?.querySelector('form');

    const currStage = stages.findIndex(s => s.curr === stageCurr)
    if (!formEl) {
      if (currStage > 0) setStageCurr(stages[currStage - 1].curr)
      return;
    }

    const data = new FormData(formEl);
    const stepPayload = {}

    for (const pair of data.entries()) {
      if (stage.curr === 3 && pair[0] === 'type') {
        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else if (stage.curr === 9 && pair[0] === 'stage') {
        stepPayload[pair[0]] = pair[1]?.toLowerCase()?.split(' ')?.[0]
      } else {
        stepPayload[pair[0]] = pair[1]
      }
    }

    const updated = { ...payload, ...stepPayload }
    setPayload(updated)

    if (currStage > 0) {
      setStageCurr(stages[currStage - 1].curr)
    }
  }

  const onUpload = (e) => {
    if (!Array.isArray(e)) return;
    setPhotos(e.filter(f => f.file));
    const remainingServerPhotos = e
      .filter(f => !f.file)
      .map(({ preview, ...rest }) => rest);
    // keep payload.photos in sync so the carry-forward below shows the correct remaining set
    setPayload(prev => ({ ...prev, photos: remainingServerPhotos }));
    // the update endpoint preserves existing photos, so removed server photos must be
    // deleted explicitly via deleteSolutionPhoto on submit — track which ones those are
    if (props.solution?.id) {
      const originalPhotos = props.solution.photos || [];
      setRemovedPhotos(originalPhotos.filter(orig =>
        !remainingServerPhotos.some(rem => rem.photo === orig.photo)
      ));
    }
  };

  if (breakpoint === 'large') {
    return (
      <Block ref={ref} position='relative' height='100vh' overflow='hidden'>
        {loading && <ReDoSpinner />}
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 2]}
          flexGridRowGap="scale800"
          height='100%'
          alignItems='center'
          overflow='hidden'
          margin='0 -28px'
        >
          <FlexGridItem position='relative' height='100%' display='flex'>
            <ReDoBanner align>
              <div className={css({ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column', padding: `${theme.sizing.scale1600} ${theme.sizing.scale3200}` })}>
                <div className={css({ paddingTop: theme.sizing.scale1600, maxWidth: '500px' })}>
                  <HeadingXLarge overrides={{ Block: { style: { lineHeight: '48px', fontWeight: '300' } } }} >{t('createSolution.heading')}</HeadingXLarge>
                  {stage.curr === 1 &&
                    <Block paddingTop='2ch'>
                      <ParagraphMedium overrides={{ Block: { style: { fontWeight: '600' } } }}>
                        {t('createSolution.language_hint')}
                      </ParagraphMedium>
                      <ParagraphMedium color={theme.colors.primary300}>
                        {t('createSolution.intro_text1')}
                      </ParagraphMedium>
                      <ParagraphMedium color={theme.colors.primary300}>
                        {t('createSolution.intro_text2')}
                      </ParagraphMedium>
                    </Block>
                  }
                </div>
                <Block display='flex' flexDirection='column' marginTop='auto'>
                  <ReDoProgressBar label={stage.label} value={stage.curr} maxValue={stage.max} />
                </Block>
              </div>
            </ReDoBanner>
          </FlexGridItem>

          <FlexGridItem height='100%' overflow='hidden'>
            <Block position='relative' height='calc(100% - 70px)' marginTop='70px'>

                <Block overrides={{ Block: { style: { zIndex: '1', pointerEvents: 'none' } } }} position='absolute' top='0px' left='0' width='100%' height='50px' backgroundImage='linear-gradient(rgb(243,242,238) 0%, rgba(255, 255, 255, 0) 100%)' />

                <form ref={scrollable} className={css({ height: '100%', overflow: 'auto' })} onSubmit={onNext}>
                  <div className={css({ paddingTop: '20px', maxWidth: '375px', margin: 'auto', paddingBottom: '28px', alignItems: 'center', display: 'flex', flexDirection: 'column', minHeight: '100%', paddingRight: '1ch' })}>

                    {stage.curr === 1 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.title_label')}
                              placeholder={t('createSolution.title_placeholder')}
                              name='title'
                              required
                              value={payload.title}
                              maxLength={20}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.summary_label')}
                              placeholder={t('createSolution.summary_placeholder')}
                              maxLength={65}
                              name='summary'
                              required
                              value={payload.summary}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 2 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.problem_label')}
                              placeholder={t('createSolution.type_here')}
                              maxLength={500}
                              name='problem'
                              required
                              value={payload.problem}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.what_label')}
                              placeholder={t('createSolution.what_placeholder')}
                              maxLength={40}
                              name='what'
                              required
                              value={payload.what}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 3 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.target_label')}
                              id="target-input"
                              name='target'
                              required
                              placeholder={t('createSolution.type_here')}
                              maxLength={400}
                              value={payload.target}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.description_label')}
                              placeholder={t('createSolution.type_here')}
                              maxLength={500}
                              name='description'
                              required
                              value={payload.description}
                            />
                          </FormControl>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.note_label')}
                              placeholder={t('createSolution.type_here')}
                              maxLength={500}
                              name='note'
                              value={payload.note}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 4 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              label={t('createSolution.value_label')}
                              placeholder={t('createSolution.type_here')}
                              maxLength={500}
                              name='createdValue'
                              required
                              value={payload.createdValue}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 5 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <ParagraphMedium marginTop='0' paddingLeft='5px'>
                            {t('createSolution.lifecycle_text_desktop')}
                          </ParagraphMedium>
                          <Diagram style={{ width: '100%', height: 'auto' }} />
                          <a className={css({ textUnderlineOffset: '.5ch', fontWeight: 'normal', fontSize: '10px', color: theme.colors.secondary200 })} href="https://blog.redo-please.com/lifecycle-thinking/" target="_blank" rel="noopener noreferrer">{t('createSolution.lifecycle_link')}</a>
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoTextarea
                              id="lifeCycle"
                              placeholder={t('createSolution.type_here')}
                              name='lifeCycle'
                              value={payload.lifeCycle}
                              maxLength={500}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 6 &&
                      <Block width='100%' marginTop={['auto', 'auto', 'auto', '5%']}>
                        <FormControl>
                          <ReDoTags
                            onChange={(e) => setPayload({ ...payload, areas: e.map(e => e.toLowerCase()) })}
                            label={t('createSolution.areas_label')}
                            options={areas}
                            limit={1}
                            additionLabel={t('createSolution.areas_add')}
                            values={payload.areas}
                            submitUpTo={4}
                          />
                        </FormControl>
                      </Block>
                    }

                    {stage.curr === 7 &&
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.why_label')}
                            placeholder={t('createSolution.why_placeholder')}
                            maxLength={500}
                            name='communityReason'
                            value={payload.communityReason}
                          />
                        </FormControl>
                      </Block>
                    }

                    {stage.curr === 8 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <FormControl>
                            <ReDoFileUpload
                              multiple
                              maxCount={3}
                              onUpload={onUpload}
                              onRemove={onUpload}
                              label={t('createSolution.visuals_label')}
                              values={payload?.photos?.map(f => ({ ...f, preview: f.photo }))}
                            />
                          </FormControl>
                        </Block>
                      </>
                    }

                    {stage.curr === 9 &&
                      <>
                        <Block width='100%' marginTop='5%'>
                          <ReDoInterests
                            label={t('createSolution.select_stage_label')}
                            options={solutionStage}
                            values={payload?.stage}
                            name='stage'
                            radio
                          />
                        </Block>
                        <Block width='100%' marginTop='5%'>
                          {/* No `name` on purpose: this checkbox is state-driven. A name would make
                              FormData(form) send it as the string "on", which fails to unmarshal into
                              the backend bool and silently leaves PublicPreview false. */}
                          <ReDoCheckbox
                            checked={!!payload.publicPreview}
                            onChange={(e) => setPayload(p => ({ ...p, publicPreview: e.target.checked }))}
                            label={t('createSolution.public_preview_label')}
                          />
                        </Block>
                      </>
                    }

                    <Block paddingBottom='50px' justifyContent='space-between' display='flex' width='100%' marginTop='5%'>
                      {stage.curr !== 1 &&
                        <ReDoButton
                          type='button'
                          onClick={onPrev}
                          kind='inverse'
                          overrides={{
                            BaseButton: {
                              style: {
                                marginTop: '0',
                                marginBottom: '0',
                                paddingLeft: '0 !important',
                                paddingTop: '0 !important',
                                paddingBottom: '0 !important',
                                backgroundColor: 'transparent',
                                color: theme.colors.black,
                                width: 'auto',
                                fontWeight: 'bold'
                              }
                            },
                            StartEnhancer: {
                              style: {
                                marginRight: '0'
                              }
                            }
                          }}
                          startEnhancer={() => <ChevronLeft size={theme.sizing.scale1000} />}
                        >
                          {t('common.back')}
                        </ReDoButton>
                      }
                      <ReDoButton
                        type='submit'
                        kind='brand'
                        overrides={{
                          BaseButton: {
                            style: {
                              marginBottom: '0',
                              paddingTop: '5px',
                              paddingBottom: '5px',
                              paddingRight: '0',
                              width: 'auto',
                              marginLeft: 'auto'
                            }
                          },
                          EndEnhancer: {
                            style: {
                              marginLeft: '0'
                            }
                          }
                        }}
                        endEnhancer={() => <ChevronRight size={theme.sizing.scale1000} />}
                      >
                        {stage.curr !== 9 ? t('common.next') : t('common.submit')}
                      </ReDoButton>
                    </Block>

                  </div>
                </form>

                <Block overrides={{ Block: { style: { pointerEvents: 'none' } } }} position='absolute' bottom='0' left='0' width='100%' height='50px' backgroundImage='linear-gradient(rgba(255, 255, 255, 0) 0%, rgb(243,242,238) 100%)' />

              </Block>
          </FlexGridItem>
        </FlexGrid>
      </Block>
    );
  } else {
    return (
      <>
        <Card
          title={t('createSolution.mobile_card_title')}
          overrides={{
            Root: {
              style: {
                height: '75%', border: 'none', borderRadius: '25px !important',
                backgroundImage: `url(${create_solution})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                margin: 'auto 0'
              }
            },
            Title: {
              style: {
                color: 'white',
                fontSize: '2rem',
                fontWeight: 'normal',
              }
            },
            Contents: {
              style: {
                height: '100%',
                margin: '0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '2ch'
              }
            }
          }}
        >
          <StyledBody>
            <ParagraphMedium color='white'>
              {t('createSolution.mobile_card_text')}
            </ParagraphMedium>
          </StyledBody>
          <StyledAction>
            <Block display='flex' alignItems='center' justifyContent='flex-end'>
              <Button onClick={() => setIsOpen(true)} shape="pill" overrides={{ BaseButton: { style: { fontWeight: 'bold', color: 'black', backgroundColor: 'white', minWidth: '30%', padding: '1ch 2ch !important', ':hover': { backgroundColor: theme.colors.secondary200 } } } }}>
                {t('createSolution.mobile_upload_btn')}
              </Button>
            </Block>
          </StyledAction>
        </Card>
        <Drawer
          isOpen={isOpen}
          autoFocus
          onClose={() => {
            setIsOpen(false)
          }}
          anchor={ANCHOR.bottom}
          size={SIZE.default}
          showBackdrop={true}
          overrides={{
            DrawerContainer: {
              style: {
                borderTopRightRadius: '32px',
                borderTopLeftRadius: '32px',
                height: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)',
                maxHeight: 'calc(100vh - var(--redo-nav-bar-height, 68px) - 120px)'
              }
            },
            DrawerBody: {
              style: {
                margin: '0 !important',
                padding: '0',
                overflow: 'hidden'
              }
            },
            Close: {
              style: {
                color: theme.colors.primary300,
                width: '48px',
                height: '48px',
              }
            }
          }}
        >
          {loading && <ReDoSpinner />}
          <div ref={scrollable} className={`${column} ${css({ padding: '3ch 0', overflow: 'auto' })}`}>

            <Block padding='0 3ch' justifyContent='space-between' display='flex' flexDirection='column'>
              <Block alignSelf='flex-end' justifyContent='space-between' display='flex'>
                <ReDoProgressBar kind='text' label={stage.label} value={stage.curr} maxValue={stage.max} />
              </Block>

              <HeadingXLarge paddingTop='1ch' margin='0'>{stage.label}</HeadingXLarge>
            </Block>

            <Block>
              <form className={css({ padding: '0 3ch', alignItems: 'center', display: 'flex', flexDirection: 'column' })} onSubmit={onNext}>
                <div className={css({ width: '100%', maxWidth: '375px', paddingBottom: '10ch' })}>
                  {stage.curr === 1 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <ParagraphMedium overrides={{ Block: { style: { fontWeight: '600', marginTop: '0' } } }}>
                          {t('createSolution.language_hint')}
                        </ParagraphMedium>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.title_label')}
                            id="title-input"
                            placeholder={t('createSolution.title_placeholder')}
                            name='title'
                            required
                            value={payload.title}
                            maxLength={20}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.summary_label')}
                            placeholder={t('createSolution.summary_placeholder')}
                            maxLength={65}
                            name='summary'
                            required
                            value={payload.summary}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 2 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.problem_label')}
                            placeholder={t('createSolution.type_here')}
                            maxLength={500}
                            name='problem'
                            required
                            value={payload.problem}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.what_label')}
                            placeholder={t('createSolution.what_placeholder')}
                            maxLength={40}
                            name='what'
                            required
                            value={payload.what}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 3 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.target_label')}
                            id="target-input"
                            name='target'
                            required
                            placeholder={t('createSolution.type_here')}
                            maxLength={40}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.description_label')}
                            placeholder={t('createSolution.type_here')}
                            maxLength={500}
                            name='description'
                            required
                            value={payload.description}
                            shadow
                          />
                        </FormControl>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.note_label')}
                            placeholder={t('createSolution.type_here')}
                            maxLength={500}
                            name='note'
                            value={payload.note}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 4 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            label={t('createSolution.value_label')}
                            placeholder={t('createSolution.type_here')}
                            maxLength={500}
                            name='createdValue'
                            required
                            value={payload.createdValue}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 5 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <ParagraphSmall marginTop='0' paddingLeft='5px'>
                          {t('createSolution.lifecycle_text_mobile')}
                        </ParagraphSmall>
                        <Diagram aria-hidden="true" style={{ display: 'block', width: '100%', maxWidth: '220px', height: 'auto', margin: '0 auto 8px' }} />
                        <a className={css({ textUnderlineOffset: '.5ch', fontWeight: 'normal', fontSize: '10px', color: theme.colors.secondary200 })} href="https://blog.redo-please.com/lifecycle-thinking/" target="_blank" rel="noopener noreferrer">{t('createSolution.lifecycle_link')}</a>
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoTextarea
                            id="lifeCycle"
                            placeholder={t('createSolution.type_here')}
                            name='lifeCycle'
                            value={payload.lifeCycle}
                            maxLength={500}
                            shadow
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 6 &&
                    <Block width='100%' marginTop='5%'>
                      <FormControl>
                        <ReDoTags
                          onChange={(e) => setPayload({ ...payload, areas: e })}
                          label={t('createSolution.areas_label')}
                          options={areas}
                          limit={1}
                          additionLabel={t('createSolution.areas_add')}
                          values={payload.areas}
                          submitUpTo={4}
                          background={theme.colors.primary100}
                        />
                      </FormControl>
                    </Block>
                  }
                  {stage.curr === 7 &&
                    <Block width='100%' marginTop='5%'>
                      <FormControl>
                        <ReDoTextarea
                          label={t('createSolution.why_label')}
                          placeholder={t('createSolution.why_placeholder')}
                          maxLength={500}
                          name='communityReason'
                          value={payload.communityReason}
                          shadow
                        />
                      </FormControl>
                    </Block>
                  }
                  {stage.curr === 8 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <FormControl>
                          <ReDoFileUpload
                            multiple
                            onUpload={onUpload}
                            onRemove={onUpload}
                            maxCount={3}
                            label={t('createSolution.visuals_label')}
                            values={payload?.photos?.map(f => ({ ...f, preview: f.photo }))}
                          />
                        </FormControl>
                      </Block>
                    </>
                  }
                  {stage.curr === 9 &&
                    <>
                      <Block width='100%' marginTop='5%'>
                        <ReDoInterests
                          label={t('createSolution.select_stage_label')}
                          options={solutionStage.map((type) => ({ ...type, color: theme.colors.primary100 }))}
                          values={payload?.stage}
                          name='stage'
                          radio
                          invert
                        />
                      </Block>
                      <Block width='100%' marginTop='5%'>
                        {/* No `name` on purpose — see the desktop branch above. */}
                        <ReDoCheckbox
                          checked={!!payload.publicPreview}
                          onChange={(e) => setPayload(p => ({ ...p, publicPreview: e.target.checked }))}
                          label={t('createSolution.public_preview_label')}
                        />
                      </Block>
                    </>
                  }
                  <Block justifyContent='space-between' display='flex' width='100%' marginTop='5%'>
                    {stage.curr !== 1 &&
                      <ReDoButton
                        type='button'
                        onClick={onPrev}
                        kind='inverse'
                        overrides={{
                          BaseButton: {
                            style: {
                              marginTop: '0',
                              marginBottom: '0',
                              paddingLeft: '0 !important',
                              paddingTop: '0 !important',
                              paddingBottom: '0 !important',
                              backgroundColor: 'transparent',
                              color: theme.colors.black,
                              width: 'auto',
                              fontWeight: 'bold'
                            }
                          },
                          StartEnhancer: {
                            style: {
                              marginRight: '0'
                            }
                          }
                        }}
                        startEnhancer={() => <ChevronLeft size={theme.sizing.scale1000} />}
                      >
                        {t('common.back')}
                      </ReDoButton>
                    }
                    <ReDoButton
                      type='submit'
                      kind='brand'
                      overrides={{
                        BaseButton: {
                          style: {
                            marginBottom: '0',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingRight: '0',
                            width: 'auto',
                            marginLeft: 'auto'
                          }
                        },
                        EndEnhancer: {
                          style: {
                            marginLeft: '0'
                          }
                        }
                      }}
                      endEnhancer={() => <ChevronRight size={theme.sizing.scale1000} />}
                    >
                      {stage.curr !== 9 ? t('common.next') : t('common.submit')}
                    </ReDoButton>
                  </Block>
                </div>
              </form>
            </Block>

          </div >
        </Drawer >
      </>
    )
  }
});

export default ReDoCreateSolution;